import { http, unwrap, type ActionResponse } from './http';
import type {
  AdminUser,
  AuthSession,
  BackendAuthResultDto,
  BackendUserProfileDto,
  LdapLoginRequest,
  LoginRequest,
} from '@features/auth/types';
import { mapBackendAuthResult, mapBackendUser } from './authMapping';

// Saf eşleme fonksiyonları `./authMapping`'e taşındı (döngüsel import kırılması
// — `http.ts` de bunları kullanır). Mevcut `@api/auth` import'ları bozulmasın
// diye buradan re-export edilirler.
export { mapBackendAuthResult, mapBackendUser } from './authMapping';

export const authApi = {
  async login(payload: LoginRequest): Promise<AuthSession> {
    const res = await http.post<ActionResponse<BackendAuthResultDto>>(
      '/api/v1/auth/admin/login',
      payload,
    );
    return mapBackendAuthResult(unwrap(res));
  },

  /**
   * FAZ-9 / MOD-18 (K-20) — LDAP/AD admin login step-1. Aynı `AuthResultDto`
   * şeklini döndürür (yerel `login` ile birebir); 2FA enrolled kullanıcıda
   * `AUTH_2FA_REQUIRED` + `data.challengeToken` fırlatır → çağıran mevcut
   * `adminLoginTwoFactor` akışına geçer. Break-glass için yerel `login` DEĞİŞMEZ.
   */
  async loginLdap(payload: LdapLoginRequest): Promise<AuthSession> {
    const res = await http.post<ActionResponse<BackendAuthResultDto>>(
      '/api/v1/auth/admin/login/ldap',
      { username: payload.username, password: payload.password, deviceId: payload.deviceId ?? null },
    );
    return mapBackendAuthResult(unwrap(res));
  },

  async logout(): Promise<void> {
    // K-8: iptal edilecek refresh token httpOnly cookie'den okunur (BE
    // `ResolveRefreshToken` cookie'yi önceler) ve BE cookie'yi temizler.
    await http.post('/api/v1/auth/logout', {
      refreshToken: null,
      allDevices: false,
    });
  },

  async me(): Promise<AdminUser> {
    const res = await http.get<ActionResponse<BackendUserProfileDto>>('/api/v1/auth/me');
    return mapBackendUser(unwrap(res));
  },

  async forgotPassword(email: string): Promise<void> {
    await http.post('/api/v1/auth/password-reset/request', { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await http.post('/api/v1/auth/password-reset/confirm', { token, newPassword });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<AuthSession> {
    // BE re-issues a fresh session on change so the caller stays signed in
    // (no logout/login round-trip). Map + return the new tokens for the store.
    const res = await http.post<ActionResponse<BackendAuthResultDto>>(
      '/api/v1/auth/change-password',
      { currentPassword, newPassword },
    );
    return mapBackendAuthResult(unwrap(res));
  },

  // --- 2FA (MOD-04 / BIZ-SEC-01) ---------------------------------------------

  /**
   * Admin login step-2. The step-1 `login` throws a `NormalizedApiError` with
   * code `AUTH_2FA_REQUIRED` carrying `data.challengeToken`; the UI collects a
   * TOTP or backup code and finalizes here. `code` accepts either — the BE
   * distinguishes.
   */
  async adminLoginTwoFactor(
    challengeToken: string,
    code: string,
    deviceId?: string | null,
  ): Promise<AuthSession> {
    const res = await http.post<ActionResponse<BackendAuthResultDto>>(
      '/api/v1/auth/admin/login/2fa',
      { challengeToken, code, deviceId: deviceId ?? null },
    );
    return mapBackendAuthResult(unwrap(res));
  },

  /** Begin self-service 2FA enrollment — returns the shared secret + otpauth URI. */
  async setupTwoFactor(): Promise<{ secret: string; provisioningUri: string }> {
    const res = await http.post<
      ActionResponse<{ secret?: string | null; provisioningUri?: string | null }>
    >('/api/v1/auth/2fa/setup');
    const d = unwrap(res);
    return { secret: d.secret ?? '', provisioningUri: d.provisioningUri ?? '' };
  },

  /** Confirm enrollment with a TOTP code — returns one-time backup codes. */
  async verifyTwoFactor(code: string): Promise<{ backupCodes: string[] }> {
    const res = await http.post<ActionResponse<{ backupCodes?: string[] | null }>>(
      '/api/v1/auth/2fa/verify',
      { code },
    );
    const d = unwrap(res);
    return { backupCodes: d.backupCodes ?? [] };
  },

  /** Disable 2FA — requires a current TOTP code. */
  async disableTwoFactor(code: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>('/api/v1/auth/2fa/disable', { code });
    unwrap(res);
  },
};
